import React, { useState, useReducer, useContext, useEffect } from "react";
import { Row, Col, Form, Container } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FormErrorState,
  FormErrorAction,
} from "types/validationError.types.ts";
import {
  validateInstallmentForm,
  validateIBAN,
  validateAccountHolder,
} from "../validationFunctions/inputValidations.tsx";

import paymentOptions from "../mockdata/paymentOptions.json";
import { PBLResponseContext, UUIDContext } from "../UUIDContexted";
import FormComponent from "./FormComponent";
import CONSTANTS from "../utils/constants";
import InstallmentOptions from "../pages/payment-methods/securedInstallmentOptions/InstallmentOptions.tsx";
import { getInstallmentOptions } from "../api/apiService.ts";
import { PaymentProcessingInstallmentLoader } from "./PaymentProcessingInstallmentLoader.tsx";
import { v4 as uuidv4 } from 'uuid';
import { clearEmptyErrors, usePaymentForm } from "../context/PaymentFormContext";
import PaymentNotSupported from "./PaymentNotSupported.tsx";

//custom error message component
export const FormFieldError: React.FC<{
  id: string;
  value: string;
  userState: string;
  testId: string;
}> = ({ id, value, userState, testId }) =>
    userState !== "typing" &&
    value !== "" && (
      <span id={id} data-testid={testId} className="error_message">
        <FormattedMessage id={value} />
      </span>
    );

const Installment: React.FC = () => {
  const PBLData = useContext(UUIDContext);
  const { setPblResponseData } = useContext(PBLResponseContext);
  const intl = useIntl();
  const [isPaymentMethodAvailable, setIsPaymentMethodAvailable] = useState(false);
  const [updatedFormData, setUpdatedFormData] = useState<any>();
  const [agreedCheckbox, setAgreedCheckbox] = useState(false);
  const [notAgreedError, setNotAgreedError] = useState("");

  const [InstallmentOptionsData, setInstallmentOptionsData] = useState<null>(null);
  const [loading, setLoading] = useState(false);
  const { PaymentFormData, setPaymentFormData, PaymentFormErrors, setPaymentFormErrors } = usePaymentForm();
  const [isEditingBillingInfo, setIsEditingBillingInfo] = useState(false);
  const [overviewAttempted, setOverviewAttempted] = useState(false);
  const [, setIsOverviewPage] = useState(false);

  const [formData, setFormData] = useState({
    address: "",
    agreed: false,
    bankaccholder: "",
    city: "",
    companyName: "",
    dob: "",
    email: "",
    firstName: "",
    formattedDate: "",
    iban: "",
    lastName: "",
    linkId: PBLData?.linkId,
    paymentMethod: "3391",
    phone: "",
    retainedBusinessValue: "",
    selectedBusiness: "Business to Customer",
    selectedCountry: "Germany",
    zipcode: "",
  });

  const [fieldVisibility, setFieldVisibility] = useState({
    isCity: false,
    isStreet: false,
    isZip: false,
    isCountryCode: false,
    isBussiness: false,
    isCompanyName: false,
    isFirstName: false,
    isLastName: false,
    isDob: false,
    isEmail: false,
    isPhone: false,
    isBankHolder: true,
    isIban: true,
    isLabelForCustomer: false,
    isLabelForContact: false,
    isLabelForBilling: true,
  });

  useEffect(() => {
    if (PaymentFormData) {
      setFormData((prevState) => ({
        ...prevState,
        address: PaymentFormData.address || "",
        city: PaymentFormData.city || "",
        dob: PaymentFormData.dob || "",
        email: PaymentFormData.email || "",
        firstName: PaymentFormData.firstName || "",
        iban: PaymentFormData.iban || "",
        lastName: PaymentFormData.lastName || "",
        phone: PaymentFormData.phone || "",
        selectedBusiness: "Business to Customer",
        selectedCountry: PaymentFormData.selectedCountry || "Germany",
        zipcode: PaymentFormData.zipcode || "",
        bankaccholder: PaymentFormData.bankaccholder || "",
      }));
    }
  }, [PaymentFormData]);

  useEffect(() => {
    setPblResponseData(null);
  }, []);

  useEffect(() => {
    const filterPaymentMethod = PBLData?.requiredParameters.find(
      (item) => item["3391"]
    );
    const dataOfInstallmentMethod = filterPaymentMethod
      ? filterPaymentMethod["3391"]
      : null;

    const updatedfieldVisibility = { ...fieldVisibility };

    dataOfInstallmentMethod?.forEach((field) => {
      if (field === "customer.billingAddress.city") {
        updatedfieldVisibility.isCity = true;
        updatedfieldVisibility.isLabelForBilling = true;
      } else if (field === "customer.billingAddress.street") {
        // Billing Address
        updatedfieldVisibility.isStreet = true;
        updatedfieldVisibility.isLabelForBilling = true;
      } else if (field === "customer.billingAddress.zip") {
        updatedfieldVisibility.isZip = true;
        updatedfieldVisibility.isLabelForBilling = true;
      } else if (field === "customer.billingAddress.countryCode") {
        updatedfieldVisibility.isCountryCode = true;
        updatedfieldVisibility.isLabelForBilling = true;
      } else if (field === "customer.personalInformation.name.firstName") {
        // Personal Information Fields
        updatedfieldVisibility.isFirstName = true;
        updatedfieldVisibility.isLabelForCustomer = true;
      } else if (field === "customer.personalInformation.name.surname") {
        updatedfieldVisibility.isLastName = true;
        updatedfieldVisibility.isLabelForCustomer = true;
      } else if (field === "customer.personalInformation.dateOfBirth") {
        updatedfieldVisibility.isDob = true;
        updatedfieldVisibility.isLabelForCustomer = true;
      } else if (field === "customer.contactDetails.emailAddress") {
        // Contact Details
        updatedfieldVisibility.isEmail = true;
        updatedfieldVisibility.isLabelForContact = true;
      } else if (field === "customer.contactDetails.phoneNumber") {
        updatedfieldVisibility.isPhone = true;
        updatedfieldVisibility.isLabelForContact = true;
      } else if (field === "customer.businessRelation") {
        updatedfieldVisibility.isBussiness = true;
        updatedfieldVisibility.isCompanyName = true;
        updatedfieldVisibility.isLabelForCustomer = true;
      }
    });

    setFieldVisibility(updatedfieldVisibility);

    if (
      PBLData &&
      PBLData.paymentMethods &&
      PBLData.paymentMethods.length > 0
    ) {
      let invoicePaymentMethodsID = paymentOptions.find(
        (option) => option.id === "installment"
      )?.paymentMethodsID;
      if (invoicePaymentMethodsID) {
        let isPaymentMethodAvailable = PBLData.paymentMethods.includes(
          invoicePaymentMethodsID
        );
        setIsPaymentMethodAvailable(isPaymentMethodAvailable);
      }
    }
  }, [PBLData]);

  //this function recurses through formErrorState and search for non-empty strings (errors) and returns true if errors not present
  const containsError = (errors: FormErrorState): boolean => {
    const check = (obj: any): boolean => {
      for (const key in obj) {
        if (typeof obj[key] === "object") {
          if (check(obj[key])) {
            return true;
          }
        } else if (typeof obj[key] === "string" && obj[key] !== "") {
          return true;
        }
      }
      return false;
    };
    return check(errors);
  };
  const [submissionAttempted, setSubmissionAttempted] = useState(false);

  const handleSubmit = () => {
    validateInstallmentForm(
      formData,
      fieldVisibility,
      false,
      errorDispatch,
      false
    );

    if (!agreedCheckbox) {
      setNotAgreedError(CONSTANTS.VALIDATION_ERRORS.DATA_PROTECTION.NOT_AGREED);
      return;
    }
    setSubmissionAttempted(true);
  };

  const handleOverviewPage = async () => {
    validateInstallmentForm(formData, fieldVisibility, false, errorDispatch,true);
    setOverviewAttempted(true);
    window.scrollTo({top: 0, behavior: 'smooth'});
  }

  function convertDateFormat(dateStr: string): string {
    const parts = dateStr.split(".");
    if (parts && parts.length > 2) {
      const [dd, mm, yyyy] = parts;
      return `${yyyy}${mm}${dd}`;
    }
    return "";
  }

  useEffect(() => {
    const partnerMerchantId = PBLData ? PBLData.bnplId : '';
    const snippetToken = `${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`;

    const makePostRequest = () => {
      if (submissionAttempted) {
        const hasError = containsError(formErrorState);

        if (!hasError) {
          const updatedFormData = {
            ...formData,
            companyName: formData.companyName.trim(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            dob: convertDateFormat(formData.dob),
            phone: formData.phone.replace(/\s/g, ""),
            address: formData.address.trim(),
            zipcode: formData.zipcode.trim(),
            city: formData.city.trim(),
            selectedBusiness: fieldVisibility.isBussiness
              ? formData.selectedBusiness == "Business to Customer"
                ? "B2C"
                : "B2B"
              : "",
            selectedCountry: fieldVisibility.isCountryCode
              ? formData.selectedCountry
              : "",
            hideInstallmentOptions: false,
          };
          setUpdatedFormData(updatedFormData);

          if (PBLData) {
            const fetchInstallmentOptions = async () => {
              setLoading(true);
              
              try {
                const installmentOptionsData = await getInstallmentOptions(updatedFormData, snippetToken);
                setInstallmentOptionsData(installmentOptionsData);
                setPaymentFormData({
                  ...PaymentFormData,
                  isOverviewPage: false,
                })
                setLoading(false);
              } catch (error) {
                setLoading(false);
                setPblResponseData({ error: error, linkId: PBLData.linkId });
              }
            };
            fetchInstallmentOptions();
          }
        }
      }
    };
    makePostRequest();

    return () => {
      setSubmissionAttempted(false);
    };
  }, [submissionAttempted]);

  const handleSelect = (eventKey: any) => {
    setFormData((prevState) => ({
      ...prevState,
      selectedBusiness: eventKey,
    }));

    if (eventKey === "Business to Customer" && formData.companyName) {
      setFormData((prevState) => ({
        ...prevState,
        retainedBusinessValue: formData.companyName,
      }));
      setFormData((prevState) => ({
        ...prevState,
        companyName: "",
      }));
    } else if (eventKey === "Business to Business") {
      setFormData((prevState) => ({
        ...prevState,
        companyName: formData.retainedBusinessValue,
      }));
    }
  };

  const handleSelectCountry = (eventKey: string | null) => {
    if (eventKey) {
      setFormData((prevState) => ({
        ...prevState,
        selectedCountry: eventKey,
      }));
    }
  };

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length > 10) {
      return;
    }

    // Code to format DOB
    let formatted = "";
    if (value.length <= 2) {
      formatted = value;
    } else if (value.length <= 4) {
      formatted = `${value.slice(0, 2)}.${value.slice(2)}`;
    } else {
      formatted = `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(
        4,
        8
      )}`;
    }

    setFormData((prevState) => ({
      ...prevState,
      dob: formatted,
    }));

    setPaymentFormData({
      ...PaymentFormData,
      dob: formatted,
    });
    const datePattern = /^\d{8}$/;

    if (datePattern.test(value)) {
      const [year, month, day] = [
        value.slice(0, 4),
        value.slice(4, 6),
        value.slice(6, 8),
      ];
      const date = new Date(`${year}-${month}-${day}`);
      if (
        date.getFullYear() === +year &&
        date.getMonth() + 1 === +month &&
        date.getDate() === +day
      ) {
        setFormData((prevState) => ({
          ...prevState,
          formattedDate: value,
        }));
      } else {
        setFormData((prevState) => ({
          ...prevState,
          formattedDate: "",
        }));
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        formattedDate: "",
      }));
    }
  };

  const updateFormData = (key: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    setPaymentFormData({
      ...PaymentFormData,
      [key]: value,
    });
  };

  const initialErrorState: FormErrorState = {
    customerError: {
      companyNameError: "",
      firstNameError: "",
      lastNameError: "",
      dobError: "",
    },
    contactError: {
      emailError: "",
      phoneError: "",
    },
    billingError: {
      addressError: "",
      zipcodeError: "",
      cityError: "",
      countryError: "",
    },
    paymentError: {
      iban: "",
      bankAccountHolderError: "",
    },
    notAgreedError: "",
  };

  const formErrorReducer = (
    errorState: FormErrorState,
    action: FormErrorAction
  ): FormErrorState => {
    switch (action.type) {
      case "SET_CUSTOMER_ERROR":
        return {
          ...errorState,
          customerError: {
            ...errorState.customerError,
            [action.field]: action.value,
          },
        };
      case "SET_CONTACT_ERROR":
        return {
          ...errorState,
          contactError: {
            ...errorState.contactError,
            [action.field]: action.value,
          },
        };
      case "SET_BILLING_ERROR":
        return {
          ...errorState,
          billingError: {
            ...errorState.billingError,
            [action.field]: action.value,
          },
        };
      case "SET_PAYMENT_ERROR":
        return {
          ...errorState,
          paymentError: {
            ...errorState.paymentError,
            [action.field]: action.value,
          },
        };
      case "SET_NOT_AGREED_ERROR":
        return {
          ...errorState,
          notAgreedError: action.value,
        };
      case "SET_ALL_ERROR":
        return {
          ...action.errors,
          notAgreedError: "",
        } as FormErrorState;
      default:
        return errorState;
    }
  };

  const [formErrorState, errorDispatch] = useReducer(
    formErrorReducer,
    initialErrorState
  );

  const [userInputState, setUserInputState] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    dob: "",
    phone: "",
    email: "",
    address: "",
    zip: "",
    city: "",
    iban: "",
    accountHolder: "",
  });

  useEffect(() => {
    if (overviewAttempted) {
      const hasError = containsError(formErrorState);
      if (!hasError) {
        setIsOverviewPage(true);
        const updatedFormData = {
          ...formData,
          selectedBusiness: formData.selectedBusiness ? formData.selectedBusiness: "Business to Customer", // Default value
          selectedCountry: formData.selectedCountry ? formData.selectedCountry: 'Germany', // Default value
          isOverviewPage: true,
        };
        setFormData(updatedFormData);
        setPaymentFormData(updatedFormData);
      }
      setOverviewAttempted(false);
    }
  }, [formErrorState, overviewAttempted]);

  useEffect(() => {
    const { notAgreedError, ...errors } = formErrorState;
    const newErrors = clearEmptyErrors(errors);
    setPaymentFormErrors(newErrors)
  }, [formErrorState]);

  useEffect(() => {
    if (PaymentFormData && PaymentFormErrors) {
      errorDispatch({
        type: 'SET_ALL_ERROR',
        errors: PaymentFormErrors
      });
    }
  }, []);

  if (!isPaymentMethodAvailable) {
    return <PaymentNotSupported />
  }

  return (
    <>
      {InstallmentOptionsData ?
        <InstallmentOptions InstallmentOptionsData={InstallmentOptionsData} updatedFormData={updatedFormData} />
      : <Container
      fluid
      className="d-flex flex-column g-0"
      data-testid="installment_payment_section"
    >
      <Row className="flex-grow-1">
        <Col>
          <FormComponent
            paymentMethodCode={3391}
            fieldVisibility={fieldVisibility}
            onSelect={handleSelect}
            formData={formData}
            onChange={updateFormData}
            handleDateChange={handleDateChange}
            handleSelectCountry={handleSelectCountry}
            formErrorState={formErrorState}
            dispatch={errorDispatch}
            userInputState={userInputState}
            setUserInputState={setUserInputState}
            isOverviewPage={PaymentFormData.isOverviewPage}
            onEditingBillingInfoChange={setIsEditingBillingInfo}
          />
        </Col>
      </Row>

      <Row className="flex-grow-1">
        {fieldVisibility.isIban && (
          <Col xs={12} md={6} className="mb-24">
            {PaymentFormData.isOverviewPage &&  !isEditingBillingInfo ? <div ><label className="form-label" data-testid="pm-iban-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.IBAN, defaultMessage: "IBAN" })} <span style={{color:"#f50057"}}>*</span></label>
            <span className='field-value-overview' >{formData.iban}</span>
          </div>:<Form.Group controlId="iban">
              <Form.Label className="" data-testid="pm-iban-label">
                {intl.formatMessage({
                  id: CONSTANTS.PAYMENT_FORM.LABEL.IBAN,
                  defaultMessage: "IBAN",
                })}{" "}
                <span style={{ color: "#F50057" }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.iban}
                onChange={(e) => {
                  updateFormData(
                    "iban",
                    e.target.value.replace(/[^\dA-Z]/g, "")
                  );
                  validateIBAN(e.target.value, errorDispatch);
                }}
                onBlur={(e) => {
                  validateIBAN(e.target.value, errorDispatch);
                  setUserInputState({ ...userInputState, iban: "" });
                }}
                onFocus={() =>
                  setUserInputState({ ...userInputState, iban: "typing" })
                }
                onKeyDown={(e) => {
                  if (e.key === " ") {
                    e.preventDefault();
                  }
                }} //preventing user from inputing whitespaces
                placeholder={intl.formatMessage({
                  id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.IBAN,
                  defaultMessage: "Enter IBAN",
                })}
                data-testid="pm-iban-input"
                className={formErrorState.paymentError.iban ? "error" : ""}
              />
              <FormFieldError
                id="iban-error"
                value={formErrorState.paymentError.iban}
                userState={userInputState.iban}
                testId="pm-iban-error"
              />
            </Form.Group>}
          </Col>
        )}
        {fieldVisibility.isBankHolder && (
          <Col xs={12} md={6} className="mb-24">
            {PaymentFormData.isOverviewPage &&  !isEditingBillingInfo ? <div ><label className="form-label" data-testid="pm-bank-holder-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.BANK_ACCOUNT_HOLDER, defaultMessage: "Bank account holder" })} <span style={{color:"#f50057"}}>*</span></label>
            <span className='field-value-overview' >{formData.bankaccholder}</span>
          </div>:<Form.Group controlId="bankAccountHolder">
              <Form.Label className="" data-testid="pm-bankaccholder-label">
                {intl.formatMessage({
                  id: CONSTANTS.PAYMENT_FORM.LABEL.BANK_ACCOUNT_HOLDER,
                  defaultMessage: "Bank account holder",
                })}{" "}
                <span style={{ color: "#F50057" }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.bankaccholder}
                onChange={(e) => {
                  updateFormData("bankaccholder", e.target.value);
                  validateAccountHolder(e.target.value, errorDispatch);
                }}
                onBlur={(e) => {
                  validateAccountHolder(e.target.value, errorDispatch);
                  setUserInputState({
                    ...userInputState,
                    accountHolder: "",
                  });
                }}
                onFocus={() =>
                  setUserInputState({
                    ...userInputState,
                    accountHolder: "typing",
                  })
                }
                placeholder={intl.formatMessage({
                  id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.BANK_ACCOUNTHOLDER,
                  defaultMessage: "Enter bank account holder",
                })}
                data-testid="pm-bankaccholder-input"
                className={
                  formErrorState.paymentError.bankAccountHolderError
                    ? "error"
                    : ""
                }
              />
              <FormFieldError
                id="accountHolderName-error"
                value={formErrorState.paymentError.bankAccountHolderError}
                userState={userInputState.accountHolder}
                testId="pm-accountHolderName-error"
              />
            </Form.Group>}
          </Col>
        )}
      </Row>
      <Row className="d-flex justify-content-center align-items-end pb-0 form-section mt-2">
        <h5 data-testid="pm-label-terms-payment" className="section-title">
          {intl.formatMessage({
            id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_INSTALLMENT_TITLE,
            defaultMessage: "Approval of the data protection notice",
          })}
        </h5>
        <Col>
          <Form.Check
            type="checkbox"
            label={
              <div
              className="term_policy_text"
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({
                    id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT_INSTALLMENT,
                    defaultMessage:
                      CONSTANTS.PAYMENT_CHECKBOX_TEXT_INSTALLMENT,
                  }),
                }}
              ></div>
            }
            className="form-sub-heading mt-3"
            checked={agreedCheckbox}
            onChange={(e) => {
              setAgreedCheckbox(e.target.checked);
              e.target.checked && setNotAgreedError("");
            }}
            data-testid="pm-agree-check"
          />
          {!agreedCheckbox && (
            <FormFieldError
              id="not-agree"
              value={notAgreedError}
              userState=""
              testId="pn-not-agree"
            />
          )}
        </Col>
        <div
          className="term_policy_text mt-3"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({
              id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_INSTALLMENT_DESCRIPTION,
              defaultMessage:
                CONSTANTS.PAYMENT_CHECKBOX_INSTALLMENT_DESCRIPTION,
            }),
          }}
        ></div>
      </Row>

      <Row className="d-flex align-items-center">

        {loading ? (
          <Col><PaymentProcessingInstallmentLoader /></Col>
        ) : (<Col className="text-end">
               <button
              className="btn btn-outline rounded-5 px-3 me-2 btn-invoice-outline"
              onClick={handleOverviewPage}
              data-testid="pm-overview-btn"
            >
            <FormattedMessage id={CONSTANTS.PAYMENT_FORM.BUTTON.GO_TO_OVERVIEW} defaultMessage="Go to overview"/>
            </button>
          <button
            className="btn btn-primary text-white px-4 rounded-4 volte-medium"
            onClick={handleSubmit}
            data-testid="pm-submit-btn"
          >
            <FormattedMessage
              id={CONSTANTS.PAYMENT_FORM.BUTTON.SHOW_INSTALLMENT_OPTIONS}
              defaultMessage="Show installment options"
            />
          </button></Col>
        )}
      </Row>
    </Container>}
    </>
  );
};

export default React.memo(Installment);
